import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api, { API } from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ArrowLeft, Pencil, Copy, Trash2, Download, Paperclip, Plus, X, FileText, Image as ImageIcon, History, Link as LinkIcon, FileUp, Lock, ClipboardCheck, User as UserIcon, Save, MessageSquare, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

export default function ExperimentDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [exp, setExp] = useState(null);
  const [observations, setObservations] = useState([]);
  const [versions, setVersions] = useState([]);
  const [newObs, setNewObs] = useState("");
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef();
  const [editingObs, setEditingObs] = useState(null);
  const [editObsContent, setEditObsContent] = useState("");
  // Results state
  const [resultContent, setResultContent] = useState("");
  const [resultAttachments, setResultAttachments] = useState([]);
  const [resultUploading, setResultUploading] = useState(false);
  const resultFileRef = useRef();
  const [savingResults, setSavingResults] = useState(false);
  const [labMentions, setLabMentions] = useState([]);

  const load = async () => {
    const [{ data: e }, { data: obs }] = await Promise.all([
      api.get(`/experiments/${id}`),
      api.get(`/experiments/${id}/observations`),
    ]);
    setExp(e);
    setObservations(obs);
    try {
      const { data: mentions } = await api.get(`/experiments/${id}/lab-mentions`);
      setLabMentions(mentions);
    } catch {}
  };

  useEffect(() => { load(); }, [id]);

  // Sync results editor when exp loads
  useEffect(() => {
    if (exp?.results) {
      setResultContent(exp.results.content || "");
      setResultAttachments(exp.results.attachments || []);
    }
  }, [exp?.id]);

  const isOwner = !!exp && !!user && exp.user_id === user.user_id;
  const canManage = isOwner || (user?.account_role === "supervisor" && !!exp && exp.user_id !== user.user_id && !!labMentions);
  // Supervisor can always manage via _can_manage on backend; UI is permissive and backend enforces.
  const canEdit = isOwner || user?.account_role === "supervisor";

  const loadVersions = async () => {
    const { data } = await api.get(`/experiments/${id}/versions`);
    setVersions(data);
  };

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push({
          file_id: data.id,
          filename: data.original_filename,
          storage_path: data.storage_path,
          content_type: data.content_type,
          size: data.size,
        });
      }
      setAttachments((a) => [...a, ...uploaded]);
      toast.success(`${uploaded.length} file(s) attached`);
    } catch (err) {
      toast.error("Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const addObs = async () => {
    if (!newObs.trim() && attachments.length === 0) return;
    try {
      await api.post(`/experiments/${id}/observations`, {
        content: newObs,
        attachments,
      });
      setNewObs("");
      setAttachments([]);
      toast.success("Observation added");
      load();
    } catch {
      toast.error("Failed to add observation");
    }
  };

  const delObs = async (oid) => {
    // Per requirement, observations are not deletable.
    toast.error("Observations cannot be deleted — they are immutable records.");
  };

  const onDuplicate = async () => {
    const { data } = await api.post(`/experiments/${id}/duplicate`);
    toast.success("Duplicated");
    navigate(`/experiments/${data.id}`);
  };

  const onDelete = async () => {
    if (!window.confirm("Delete this experiment? This cannot be undone.")) return;
    await api.delete(`/experiments/${id}`);
    toast.success("Deleted");
    navigate("/experiments");
  };

  const exportFile = async (format) => {
    try {
      const res = await fetch(`${API}/experiments/${id}/export?format=${format}`, { credentials: "include" });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${exp.title}.${format}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${format.toUpperCase()}`);
    } catch (err) {
      toast.error("Export failed");
    }
  };

  const startEditObs = (o) => {
    setEditingObs(o.id);
    setEditObsContent(o.content);
  };
  const saveEditObs = async () => {
    if (!editObsContent.trim()) return toast.error("Content cannot be empty");
    try {
      await api.put(`/observations/${editingObs}`, { content: editObsContent });
      toast.success("Observation updated");
      setEditingObs(null);
      setEditObsContent("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to edit");
    }
  };

  // Result file upload
  const onResultUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setResultUploading(true);
    try {
      const uploaded = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push({
          file_id: data.id, filename: data.original_filename,
          storage_path: data.storage_path, content_type: data.content_type, size: data.size,
        });
      }
      setResultAttachments((a) => [...a, ...uploaded]);
      toast.success(`${uploaded.length} file(s) attached to results`);
    } catch { toast.error("Upload failed"); }
    finally {
      setResultUploading(false);
      if (resultFileRef.current) resultFileRef.current.value = "";
    }
  };

  const saveResults = async (markCompleted = false) => {
    setSavingResults(true);
    try {
      const payload = {
        results: {
          content: resultContent,
          attachments: resultAttachments,
          completed_at: markCompleted ? new Date().toISOString() : (exp.results?.completed_at || null),
        },
      };
      if (markCompleted) payload.status = "Completed";
      await api.put(`/experiments/${id}`, payload);
      toast.success(markCompleted ? "Experiment marked completed" : "Results saved");
      load();
    } catch {
      toast.error("Save failed");
    } finally { setSavingResults(false); }
  };

  if (!exp) return <div className="p-8 text-sm font-mono text-zinc-500">loading…</div>;

  return (
    <div className="p-6 md:p-10 max-w-5xl mx-auto space-y-6 animate-fade-in" data-testid="experiment-detail-page">
      <div className="flex items-center justify-between">
        <Link to="/experiments" className="text-sm font-mono text-zinc-500 hover:text-zinc-900 dark:hover:text-zinc-50 inline-flex items-center gap-1">
          <ArrowLeft className="h-4 w-4" /> All experiments
        </Link>
        <div className="flex gap-2">
          <Dialog>
            <DialogTrigger asChild>
              <Button variant="outline" size="sm" data-testid="export-btn"><Download className="h-4 w-4 mr-2" />Export</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Export experiment</DialogTitle>
                <DialogDescription>Choose a format for the structured report.</DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-3 py-2">
                <button
                  onClick={() => exportFile("pdf")}
                  className="border border-zinc-200 dark:border-zinc-800 rounded-md p-4 hover:border-blue-500 transition-colors text-left"
                  data-testid="export-pdf-btn"
                >
                  <FileText className="h-5 w-5 text-red-600 mb-2" />
                  <div className="font-sans font-semibold">PDF</div>
                  <div className="text-xs font-mono text-zinc-500 mt-1">Manuscript-ready</div>
                </button>
                <button
                  onClick={() => exportFile("docx")}
                  className="border border-zinc-200 dark:border-zinc-800 rounded-md p-4 hover:border-blue-500 transition-colors text-left"
                  data-testid="export-docx-btn"
                >
                  <FileText className="h-5 w-5 text-blue-600 mb-2" />
                  <div className="font-sans font-semibold">DOCX</div>
                  <div className="text-xs font-mono text-zinc-500 mt-1">Editable Word document</div>
                </button>
              </div>
            </DialogContent>
          </Dialog>
          <Button variant="outline" size="sm" onClick={onDuplicate} data-testid="duplicate-btn"><Copy className="h-4 w-4 mr-2" />Duplicate</Button>
          <Link to={`/experiments/${id}/edit`}>
            <Button variant="outline" size="sm" data-testid="edit-btn"><Pencil className="h-4 w-4 mr-2" />Edit</Button>
          </Link>
          <Button variant="outline" size="sm" onClick={onDelete} className="hover:text-red-600" data-testid="delete-btn"><Trash2 className="h-4 w-4" /></Button>
        </div>
      </div>

      {/* Header */}
      <div className="border-b border-zinc-200 dark:border-zinc-800 pb-6">
        <div className="flex flex-wrap items-center gap-2 text-xs font-mono text-zinc-500">
          <span>{exp.id.slice(0, 8)}</span>
          <span>·</span>
          <span>Updated {new Date(exp.updated_at).toLocaleString()}</span>
          {exp.is_template && <><span>·</span><span className="text-blue-600">Template</span></>}
        </div>
        <h1 className="text-3xl sm:text-4xl font-sans font-bold tracking-tight mt-2">{exp.title}</h1>
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <StatusBadge status={exp.status} />
          {exp.start_date && <span className="text-xs font-mono text-zinc-500">Start: {exp.start_date}</span>}
          {exp.end_date && <span className="text-xs font-mono text-zinc-500">End: {exp.end_date}</span>}
          {exp.tags?.map((t) => (
            <span key={t} className="text-xs font-mono text-zinc-500 bg-zinc-100 dark:bg-zinc-800 rounded px-2 py-0.5">#{t}</span>
          ))}
        </div>
      </div>

      {labMentions.length > 0 && (
        <div className="rounded-md border border-blue-200 dark:border-blue-900/50 bg-blue-50/60 dark:bg-blue-950/20 p-4" data-testid="lab-mentions-card">
          <div className="flex items-start gap-3">
            <MessageSquare className="h-4 w-4 text-blue-600 mt-1" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <div className="text-xs font-mono uppercase tracking-[0.2em] text-blue-600">Discussed in labs</div>
                <ShieldCheck className="h-3 w-3 text-emerald-600" title="End-to-end encrypted" />
              </div>
              <div className="mt-2 flex flex-wrap gap-2">
                {labMentions.map((lm) => (
                  <Link
                    key={lm.lab_id}
                    to={`/labs/${lm.lab_id}`}
                    className="group inline-flex items-center gap-2 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 hover:border-blue-500 rounded-md px-3 py-2 transition-colors"
                    data-testid={`lab-mention-${lm.lab_id}`}
                  >
                    <MessageSquare className="h-3.5 w-3.5 text-blue-600" />
                    <div className="text-left min-w-0">
                      <div className="font-sans font-medium text-sm truncate group-hover:text-blue-600 transition-colors">
                        {lm.lab_name}
                      </div>
                      <div className="text-[10px] font-mono text-zinc-500 truncate">
                        {lm.count} update{lm.count !== 1 ? "s" : ""} · last by {lm.latest_sender || "—"} · {new Date(lm.latest_at).toLocaleDateString()}
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
              <div className="mt-2 text-[10px] font-mono text-zinc-500">
                Message contents are end-to-end encrypted. Open the lab and unlock to view.
              </div>
            </div>
          </div>
        </div>
      )}

      <Tabs defaultValue="overview" onValueChange={(v) => { if (v === "history") loadVersions(); }}>
        <TabsList>
          <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
          <TabsTrigger value="observations" data-testid="tab-observations">Observations ({observations.length})</TabsTrigger>
          <TabsTrigger value="results" data-testid="tab-results">Results</TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">History</TabsTrigger>
        </TabsList>        <TabsContent value="overview" className="space-y-8 pt-6">
          {exp.objective && (
            <Section title="Objective"><p className="font-body text-zinc-700 dark:text-zinc-300 leading-relaxed">{exp.objective}</p></Section>
          )}
          {exp.hypothesis && (
            <Section title="Hypothesis"><p className="font-body text-zinc-700 dark:text-zinc-300 leading-relaxed">{exp.hypothesis}</p></Section>
          )}
          {exp.materials?.length > 0 && (
            <Section title="Materials">
              <ul className="space-y-1">
                {exp.materials.map((m, i) => (
                  <li key={i} className="font-body text-zinc-700 dark:text-zinc-300 flex gap-3">
                    <span className="text-zinc-400 font-mono text-xs pt-1 w-6">{String(i + 1).padStart(2, "0")}</span>
                    {m}
                  </li>
                ))}
              </ul>
            </Section>
          )}
          {exp.protocol?.length > 0 && (
            <Section title="Protocol">
              <ol className="space-y-3">
                {exp.protocol.map((s, i) => (
                  <li key={i} className="flex gap-4 border-l-2 border-blue-600/20 pl-4 py-1">
                    <span className="text-xs font-mono text-blue-600 pt-1">{String(i + 1).padStart(2, "0")}</span>
                    <p className="font-body text-zinc-700 dark:text-zinc-300 flex-1">{s.text}</p>
                  </li>
                ))}
              </ol>
            </Section>
          )}
          {exp.references?.length > 0 && (
            <Section title="References">
              <ul className="space-y-2">
                {exp.references.map((r, i) => (
                  <li key={i} className="flex items-start gap-2 font-body text-zinc-700 dark:text-zinc-300" data-testid={`ref-display-${i}`}>
                    {r.kind === "link" ? <LinkIcon className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" /> : <FileUp className="h-4 w-4 text-blue-600 mt-0.5 flex-shrink-0" />}
                    <div className="min-w-0 flex-1">
                      {r.kind === "link" ? (
                        <a href={r.url} target="_blank" rel="noreferrer" className="hover:text-blue-600 underline decoration-zinc-300 dark:decoration-zinc-700 underline-offset-2">{r.label || r.url}</a>
                      ) : (
                        <a href={`${API}/files/${r.file_id}`} target="_blank" rel="noreferrer" className="hover:text-blue-600 underline decoration-zinc-300 dark:decoration-zinc-700 underline-offset-2">{r.label || r.filename}</a>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
            </Section>
          )}
          {exp.remarks && (
            <Section title="Remarks">
              <p className="font-body text-zinc-700 dark:text-zinc-300 leading-relaxed whitespace-pre-wrap">{exp.remarks}</p>
            </Section>
          )}
        </TabsContent>

        {/* Results tab */}
        <TabsContent value="results" className="space-y-6 pt-6">
          {!canEdit ? (
            <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-md p-5 space-y-4">
              {exp.results?.content || exp.results?.attachments?.length ? (
                <>
                  {exp.results?.content && (
                    <p className="font-body text-zinc-800 dark:text-zinc-200 whitespace-pre-wrap">{exp.results.content}</p>
                  )}
                  {exp.results?.attachments?.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {exp.results.attachments.map((a) => (
                        <a key={a.file_id} href={`${API}/files/${a.file_id}`} target="_blank" rel="noreferrer" className="text-xs font-mono inline-flex items-center gap-1.5 border border-zinc-200 dark:border-zinc-800 rounded px-2 py-1 hover:border-blue-500">
                          {a.content_type?.startsWith("image/") ? <ImageIcon className="h-3 w-3" /> : <FileText className="h-3 w-3" />}
                          {a.filename}
                        </a>
                      ))}
                    </div>
                  )}
                  {exp.results?.completed_at && (
                    <div className="text-xs font-mono text-emerald-600 inline-flex items-center gap-1">
                      <ClipboardCheck className="h-3 w-3" /> Completed {new Date(exp.results.completed_at).toLocaleString()}
                    </div>
                  )}
                </>
              ) : (
                <div className="text-sm font-mono text-zinc-500">no results recorded yet</div>
              )}
            </div>
          ) : (
            <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-md p-5 space-y-3" data-testid="results-editor">
              <div className="flex items-center gap-2">
                <ClipboardCheck className="h-4 w-4 text-blue-600" />
                <h2 className="font-sans text-sm font-semibold uppercase tracking-widest text-zinc-600 dark:text-zinc-400">
                  Results
                </h2>
                {exp.results?.completed_at && (
                  <span className="text-[10px] font-mono uppercase tracking-widest text-emerald-600 border border-emerald-200 dark:border-emerald-900/60 bg-emerald-50 dark:bg-emerald-950/40 rounded px-1.5 py-0.5 ml-auto">
                    locked-in {new Date(exp.results.completed_at).toLocaleDateString()}
                  </span>
                )}
              </div>
              <Textarea
                rows={6}
                value={resultContent}
                onChange={(e) => setResultContent(e.target.value)}
                placeholder="Summarize findings, key measurements, conclusions…"
                data-testid="results-content-input"
              />
              {resultAttachments.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {resultAttachments.map((a, i) => (
                    <span key={i} className="text-xs font-mono bg-zinc-100 dark:bg-zinc-800 rounded px-2 py-1 flex items-center gap-2">
                      <Paperclip className="h-3 w-3" /> {a.filename}
                      <button onClick={() => setResultAttachments((arr) => arr.filter((_, idx) => idx !== i))} className="text-zinc-400 hover:text-red-600">
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ))}
                </div>
              )}
              <div className="flex items-center justify-between pt-2 border-t border-zinc-200 dark:border-zinc-800">
                <input type="file" ref={resultFileRef} onChange={onResultUpload} multiple hidden data-testid="results-file-input" />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => resultFileRef.current?.click()}
                  disabled={resultUploading}
                  data-testid="results-attach-btn"
                >
                  <Paperclip className="h-4 w-4 mr-2" /> {resultUploading ? "Uploading…" : "Attach files"}
                </Button>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => saveResults(false)}
                    disabled={savingResults}
                    data-testid="save-results-btn"
                  >
                    <Save className="h-4 w-4 mr-2" /> Save
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => saveResults(true)}
                    disabled={savingResults}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="complete-experiment-btn"
                  >
                    <ClipboardCheck className="h-4 w-4 mr-2" /> Save & mark Completed
                  </Button>
                </div>
              </div>
              <div className="text-xs font-mono text-zinc-500 pt-1">
                Tip: a comprehensive PDF/DOCX report including references, observations and these results
                can be downloaded from the Export button above.
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="observations" className="space-y-6 pt-6">
          <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-md p-4 space-y-3">
            <Textarea
              rows={3}
              value={newObs}
              onChange={(e) => setNewObs(e.target.value)}
              placeholder="Log an observation, measurement, or note…"
              data-testid="observation-input"
            />
            {attachments.length > 0 && (
              <div className="flex flex-wrap gap-2">
                {attachments.map((a, i) => (
                  <span key={i} className="text-xs font-mono bg-zinc-100 dark:bg-zinc-800 rounded px-2 py-1 flex items-center gap-2">
                    <Paperclip className="h-3 w-3" /> {a.filename}
                    <button onClick={() => setAttachments((arr) => arr.filter((_, idx) => idx !== i))} className="text-zinc-400 hover:text-red-600">
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
            <div className="flex items-center justify-between">
              <input type="file" ref={fileRef} onChange={onUpload} multiple hidden data-testid="file-input" />
              <Button
                variant="outline"
                size="sm"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                data-testid="attach-file-btn"
              >
                <Paperclip className="h-4 w-4 mr-2" /> {uploading ? "Uploading…" : "Attach files"}
              </Button>
              <Button onClick={addObs} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="add-observation-btn">
                <Plus className="h-4 w-4 mr-2" /> Add observation
              </Button>
            </div>
          </div>

          <ul className="space-y-3">
            {observations.length === 0 && (
              <div className="text-center py-10 text-sm font-mono text-zinc-500">no observations yet</div>
            )}
            {observations.map((o) => {
              const isAuthor = o.user_id === user?.user_id;
              const isEditing = editingObs === o.id;
              return (
                <li key={o.id} className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-md p-4" data-testid={`observation-${o.id}`}>
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1 min-w-0">
                      {isEditing ? (
                        <div className="space-y-2">
                          <Textarea
                            rows={3}
                            value={editObsContent}
                            onChange={(e) => setEditObsContent(e.target.value)}
                            data-testid={`edit-obs-input-${o.id}`}
                          />
                          <div className="flex gap-2">
                            <Button size="sm" onClick={saveEditObs} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid={`save-obs-edit-${o.id}`}>
                              Save edit
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => { setEditingObs(null); setEditObsContent(""); }}>
                              Cancel
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <p className="font-body text-zinc-800 dark:text-zinc-200 whitespace-pre-wrap">{o.content}</p>
                      )}
                      {o.attachments?.length > 0 && (
                        <div className="flex flex-wrap gap-2 mt-3">
                          {o.attachments.map((a) => (
                            <a
                              key={a.file_id}
                              href={`${API}/files/${a.file_id}`}
                              target="_blank"
                              rel="noreferrer"
                              className="text-xs font-mono inline-flex items-center gap-1.5 border border-zinc-200 dark:border-zinc-800 rounded px-2 py-1 hover:border-blue-500 transition-colors"
                            >
                              {a.content_type?.startsWith("image/") ? <ImageIcon className="h-3 w-3" /> : <FileText className="h-3 w-3" />}
                              {a.filename}
                            </a>
                          ))}
                        </div>
                      )}
                      <div className="flex items-center flex-wrap gap-3 text-xs font-mono text-zinc-500 mt-2">
                        <span className="inline-flex items-center gap-1"><UserIcon className="h-3 w-3" /> {o.user_name || "—"}</span>
                        <span>·</span>
                        <span>{new Date(o.created_at).toLocaleString()}</span>
                        {o.last_edited_at && (
                          <>
                            <span>·</span>
                            <Popover>
                              <PopoverTrigger asChild>
                                <button className="inline-flex items-center gap-1 text-amber-600 hover:underline" data-testid={`edit-history-btn-${o.id}`}>
                                  <Pencil className="h-3 w-3" /> edited {new Date(o.last_edited_at).toLocaleString()} ({o.edits?.length || 0})
                                </button>
                              </PopoverTrigger>
                              <PopoverContent align="start" className="w-96 p-0">
                                <div className="p-3 border-b border-zinc-200 dark:border-zinc-800 text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                                  Edit history (oldest first)
                                </div>
                                <div className="max-h-72 overflow-y-auto divide-y divide-zinc-200 dark:divide-zinc-800">
                                  {(o.edits || []).map((e, i) => (
                                    <div key={i} className="p-3">
                                      <div className="text-[10px] font-mono text-zinc-500">
                                        previous content · {new Date(e.edited_at).toLocaleString()}
                                      </div>
                                      <p className="font-body text-sm whitespace-pre-wrap mt-1 text-zinc-700 dark:text-zinc-300">{e.content}</p>
                                    </div>
                                  ))}
                                </div>
                              </PopoverContent>
                            </Popover>
                          </>
                        )}
                      </div>
                    </div>
                    {isAuthor && !isEditing && (
                      <Button variant="ghost" size="icon" onClick={() => startEditObs(o)} title="Edit observation" data-testid={`edit-obs-${o.id}`}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </TabsContent>

        <TabsContent value="history" className="pt-6">
          <div className="bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-800 rounded-md">
            {versions.length === 0 ? (
              <div className="text-center py-10 text-sm font-mono text-zinc-500">no history available</div>
            ) : (
              <ul className="divide-y divide-zinc-200 dark:divide-zinc-800">
                {versions.map((v, i) => (
                  <li key={i} className="p-4 flex items-center gap-3">
                    <History className="h-4 w-4 text-zinc-400" />
                    <div className="flex-1">
                      <div className="font-body text-sm capitalize">{v.action}</div>
                      <div className="text-xs font-mono text-zinc-500">{new Date(v.at).toLocaleString()}</div>
                    </div>
                    <span className="text-xs font-mono text-zinc-500">
                      Status: {v.snapshot?.status}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section>
      <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-zinc-500 mb-3">{title}</div>
      {children}
    </section>
  );
}
